/**
 * PDF save helper with user gesture timeout handling and fallback options.
 * 
 * @param {Blob} pdfBlob - The PDF blob to save
 * @param {string} defaultFileName - Default filename for the PDF
 * @param {Object} options - Configuration options
 * @param {boolean} options.forceFallback - Force use of fallback download method
 * @param {boolean} options.skipFilePicker - Skip showSaveFilePicker attempt entirely
 * @param {number} options.userGestureTs - Timestamp when user gesture occurred (milliseconds)  
 * @param {number} options.gestureTimeoutMs - Maximum time allowed since user gesture (default: 5000ms)
 * @returns {Promise<void>}
 */
export async function savePdfBlob(pdfBlob, defaultFileName = "penetration-resultat.pdf", options = {}) {
  const {
    forceFallback = false,
    skipFilePicker = false,
    userGestureTs = null,
    gestureTimeoutMs = 5000
  } = options;

  // Check if we should attempt showSaveFilePicker
  const shouldTryFilePicker = !forceFallback && !skipFilePicker && window.showSaveFilePicker;
  
  // Check if user gesture is still likely active
  const isGestureActive = userGestureTs === null || 
    (Date.now() - userGestureTs) < gestureTimeoutMs;

  if (shouldTryFilePicker && isGestureActive) {
    try {
      const filePickerOptions = {
        suggestedName: defaultFileName,
        types: [{
          description: "PDF Files",
          accept: { "application/pdf": [".pdf"] }
        }]
      };
      const handle = await window.showSaveFilePicker(filePickerOptions);
      const writable = await handle.createWritable();
      await writable.write(pdfBlob);
      await writable.close();
      return;
    } catch (e) {
      if (e.name !== "AbortError") {
        // User gesture timeout or other error - fall back to download
        console.warn("showSaveFilePicker failed, falling back to download:", e.message);
        // Don't show alert for gesture timeout errors as they're expected for large exports
        if (!e.message.includes("user gesture")) {
          alert("Kunde inte spara PDF-filen: " + e.message);
        }
      }
      // Fall through to fallback method
    }
  }
  
  // Fallback: standard download method
  const url = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = defaultFileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}